import { Building2, Edit, Plus, Trash2 } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { toast } from 'react-hot-toast';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import api from '../../services/api';

const CATEGORY_LABELS = {
  economy: 'Economy',
  basic: 'Basic',
  regular: 'Regular',
  premium: 'Premium'
};

const BILLING_CYCLE_LABELS = {
  monthly: '월간',
  quarterly: '분기',
  annual: '연간'
};

const FEE_TYPE_LABELS = {
  per_unit_flat: '세대당 정액',
  monthly_rent_percentage: '월세금액 비율',
  building_flat: '건물당 정액',
  property_owner_flat: '임대사업자 월 정액'
};

const OPEN_ENDED_DATE = '9999-12-31';

const formatCurrency = (value) => `${Number(value || 0).toLocaleString()}원`;

const formatPolicyEndDate = (value) => (!value || value === OPEN_ENDED_DATE ? '무기한' : value);

const parseJsonObject = (value, fallback = {}) => {
  if (!value) return fallback;
  if (typeof value === 'object') return value;
  try {
    return JSON.parse(value);
  } catch (error) {
    return fallback;
  }
};

export const formatManagementServicePlan = (plan) => {
  const feeType = plan?.fee_calculation_type || 'per_unit_flat';
  if (feeType === 'monthly_rent_percentage') {
    const minMax = [
      Number(plan?.min_fee_amount || 0) > 0 ? `최소 ${formatCurrency(plan.min_fee_amount)}` : null,
      Number(plan?.max_fee_amount || 0) > 0 ? `최대 ${formatCurrency(plan.max_fee_amount)}` : null
    ].filter(Boolean);
    return `${FEE_TYPE_LABELS[feeType]} ${Number(plan?.percentage_rate || 0)}%${minMax.length ? ` (${minMax.join(', ')})` : ''}`;
  }
  if (feeType === 'building_flat') {
    return `${FEE_TYPE_LABELS[feeType]} ${formatCurrency(plan?.flat_fee_amount)}`;
  }
  if (feeType === 'property_owner_flat') {
    return `${FEE_TYPE_LABELS[feeType]} ${formatCurrency(plan?.flat_fee_amount)}`;
  }
  return `${FEE_TYPE_LABELS[feeType] || FEE_TYPE_LABELS.per_unit_flat} ${formatCurrency(plan?.base_fee_per_unit)}`;
};

const ManagementServicePlanPolicy = ({ compact = false }) => {
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const queryClient = useQueryClient();

  const { data: plansData, isLoading } = useQuery(
    'management-service-plans',
    async () => {
      const res = await api.get('/management-service-billing/plans');
      return res.plans || res || [];
    },
    {
      onError: (error) => {
        console.error('요금정책 조회 실패:', error);
        toast.error('요금정책 목록을 불러오지 못했습니다.');
      }
    }
  );

  const plans = plansData || [];

  const createPlanMutation = useMutation(
    (data) => api.post('/management-service-billing/plans', data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('management-service-plans');
        setShowCreateModal(false);
        toast.success('요금정책을 등록했습니다.');
      },
      onError: (error) => {
        toast.error(error.response?.data?.error || '요금정책 등록에 실패했습니다.');
      }
    }
  );

  const updatePlanMutation = useMutation(
    ({ planId, data }) => api.put(`/management-service-billing/plans/${planId}`, data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('management-service-plans');
        setShowEditModal(false);
        setSelectedPlan(null);
        toast.success('요금정책을 수정했습니다.');
      },
      onError: (error) => {
        toast.error(error.response?.data?.error || '요금정책 수정에 실패했습니다.');
      }
    }
  );

  const deletePlanMutation = useMutation(
    (planId) => api.delete(`/management-service-billing/plans/${planId}`),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('management-service-plans');
        toast.success('요금정책을 삭제했습니다.');
      },
      onError: (error) => {
        toast.error(error.response?.data?.error || '요금정책 삭제에 실패했습니다.');
      }
    }
  );

  const handleDelete = (planId) => {
    if (window.confirm('이 요금정책을 삭제하시겠습니까?')) {
      deletePlanMutation.mutate(planId);
    }
  };

  if (isLoading) {
    return (
      <div className="bg-white rounded-lg shadow p-8 text-center">
        <div className="loading-spinner mx-auto"></div>
        <p className="mt-2 text-gray-600">요금정책 목록을 불러오는 중...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="text-xl font-bold text-gray-900">임대관리요금정책 설정</h2>
          <p className="mt-1 text-sm text-gray-600">
            임대사업자에게 청구할 임대위탁대행 수수료 정책을 미리 등록합니다.
          </p>
        </div>
        <button
          type="button"
          onClick={() => setShowCreateModal(true)}
          className="inline-flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
        >
          <Plus className="mr-2 h-4 w-4" />
          요금정책 등록
        </button>
      </div>

      {!compact && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-blue-50 border border-blue-100 rounded-lg p-4">
            <p className="text-sm font-medium text-blue-900">계산 기준 선택</p>
            <p className="mt-1 text-sm text-blue-700">세대당, 월세 비율, 건물당, 임대사업자 월 정액 중 선택합니다.</p>
          </div>
          <div className="bg-green-50 border border-green-100 rounded-lg p-4">
            <p className="text-sm font-medium text-green-900">고객별 적용</p>
            <p className="mt-1 text-sm text-green-700">소속임대사업자관리에서 기본 정책을 지정하고 세대별 예외도 둘 수 있습니다.</p>
          </div>
          <div className="bg-purple-50 border border-purple-100 rounded-lg p-4">
            <p className="text-sm font-medium text-purple-900">청구 자동 반영</p>
            <p className="mt-1 text-sm text-purple-700">청구 생성 시 적용된 정책 기준으로 기본 수수료가 계산됩니다.</p>
          </div>
        </div>
      )}

      <div className="bg-white shadow rounded-lg overflow-hidden">
        {plans.length === 0 ? (
          <div className="p-8 text-center">
            <Building2 className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">등록된 요금정책이 없습니다</h3>
            <p className="mt-1 text-sm text-gray-500">임대사업자에게 적용할 요금정책을 먼저 등록해보세요.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">구분</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">정책명</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">계산 기준</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">청구 설정</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">적용기간</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">작업</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {plans.map((plan) => (
                  <tr key={plan.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800">
                        {CATEGORY_LABELS[plan.plan_category] || plan.plan_category || 'Regular'}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm font-medium text-gray-900">{plan.plan_name}</div>
                      {plan.description && <div className="text-sm text-gray-500">{plan.description}</div>}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {formatManagementServicePlan(plan)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {BILLING_CYCLE_LABELS[plan.billing_cycle] || plan.billing_cycle} · 매월 {plan.billing_day || 1}일 · 납부 {plan.due_days || 7}일
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {plan.effective_from || '상시'} ~ {formatPolicyEndDate(plan.effective_to)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex justify-end space-x-2">
                        <button
                          type="button"
                          onClick={() => {
                            setSelectedPlan(plan);
                            setShowEditModal(true);
                          }}
                          className="text-blue-600 hover:text-blue-900"
                          title="수정"
                        >
                          <Edit className="h-4 w-4" />
                        </button>
                        <button
                          type="button"
                          onClick={() => handleDelete(plan.id)}
                          className="text-red-600 hover:text-red-900"
                          title="삭제"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <PolicyAssignmentPanel plans={plans} />

      {showCreateModal && (
        <ServicePlanPolicyModal
          onClose={() => setShowCreateModal(false)}
          onSubmit={(data) => createPlanMutation.mutate(data)}
          isLoading={createPlanMutation.isLoading}
        />
      )}

      {showEditModal && selectedPlan && (
        <ServicePlanPolicyModal
          plan={selectedPlan}
          onClose={() => {
            setShowEditModal(false);
            setSelectedPlan(null);
          }}
          onSubmit={(data) => updatePlanMutation.mutate({ planId: selectedPlan.id, data })}
          isLoading={updatePlanMutation.isLoading}
        />
      )}
    </div>
  );
};

const PolicyAssignmentPanel = ({ plans }) => {
  const [propertyOwnerId, setPropertyOwnerId] = useState('');
  const [buildingId, setBuildingId] = useState('');
  const [scope, setScope] = useState('property_owner');
  const [servicePlanId, setServicePlanId] = useState('');
  const [selectedUnitIds, setSelectedUnitIds] = useState([]);
  const queryClient = useQueryClient();

  const { data: assignmentData, isLoading } = useQuery(
    ['management-service-plan-assignments', propertyOwnerId, buildingId],
    async () => {
      const params = new URLSearchParams();
      if (propertyOwnerId) params.set('property_owner_id', propertyOwnerId);
      if (buildingId) params.set('building_id', buildingId);
      return api.get(`/management-service-billing/plan-assignments?${params.toString()}`);
    },
    {
      keepPreviousData: true,
      onError: (error) => {
        console.error('요금정책 적용 대상 조회 실패:', error);
        toast.error('요금정책 적용 대상을 불러오지 못했습니다.');
      }
    }
  );

  const assignmentPlans = assignmentData?.plans || plans || [];
  const propertyOwners = assignmentData?.property_owners || [];
  const buildings = assignmentData?.buildings || [];
  const units = assignmentData?.units || [];

  useEffect(() => {
    setBuildingId('');
    setSelectedUnitIds([]);
  }, [propertyOwnerId]);

  useEffect(() => {
    setSelectedUnitIds([]);
  }, [buildingId, scope]);

  const applyMutation = useMutation(
    (payload) => api.post('/management-service-billing/plan-assignments', payload),
    {
      onSuccess: (res) => {
        queryClient.invalidateQueries('management-service-plan-assignments');
        queryClient.invalidateQueries('management-service-plans');
        toast.success(res?.message || '요금정책 적용을 저장했습니다.');
      },
      onError: (error) => {
        toast.error(error.response?.data?.error || '요금정책 적용 저장에 실패했습니다.');
      }
    }
  );

  const handleUnitToggle = (unitId) => {
    setSelectedUnitIds((prev) => (
      prev.includes(unitId)
        ? prev.filter((id) => id !== unitId)
        : [...prev, unitId]
    ));
  };

  const handleApply = () => {
    if (!propertyOwnerId) {
      toast.error('임대사업자를 선택해주세요.');
      return;
    }
    if (['building', 'units'].includes(scope) && !buildingId) {
      toast.error('건물을 선택해주세요.');
      return;
    }
    if (scope === 'units' && selectedUnitIds.length === 0) {
      toast.error('세대를 선택해주세요.');
      return;
    }

    applyMutation.mutate({
      scope,
      property_owner_id: parseInt(propertyOwnerId, 10),
      building_id: buildingId ? parseInt(buildingId, 10) : null,
      unit_ids: selectedUnitIds,
      service_plan_id: servicePlanId ? parseInt(servicePlanId, 10) : null
    });
  };

  const selectedOwner = propertyOwners.find((owner) => String(owner.id) === String(propertyOwnerId));
  const selectedBuilding = buildings.find((building) => String(building.id) === String(buildingId));

  return (
    <div className="bg-white shadow rounded-lg p-6">
      <div className="flex flex-col gap-1">
        <h3 className="text-lg font-semibold text-gray-900">요금정책 적용</h3>
        <p className="text-sm text-gray-600">
          임대사업자 기본 정책을 먼저 지정하고, 필요한 경우 건물 또는 세대 단위로 예외 정책을 적용합니다.
        </p>
      </div>

      <div className="mt-5 grid grid-cols-1 lg:grid-cols-4 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700">임대사업자</label>
          <select
            value={propertyOwnerId}
            onChange={(e) => setPropertyOwnerId(e.target.value)}
            className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
          >
            <option value="">선택</option>
            {propertyOwners.map((owner) => (
              <option key={owner.id} value={owner.id}>
                {owner.company_name || owner.name}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700">적용 레벨</label>
          <select
            value={scope}
            onChange={(e) => setScope(e.target.value)}
            className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
          >
            <option value="property_owner">임대사업자 기본</option>
            <option value="building">건물</option>
            <option value="units">세대 예외</option>
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700">건물</label>
          <select
            value={buildingId}
            onChange={(e) => setBuildingId(e.target.value)}
            disabled={!propertyOwnerId || scope === 'property_owner'}
            className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 disabled:bg-gray-100"
          >
            <option value="">선택</option>
            {buildings.map((building) => (
              <option key={building.id} value={building.id}>
                {building.name}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700">적용 요금정책</label>
          <select
            value={servicePlanId}
            onChange={(e) => setServicePlanId(e.target.value)}
            className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
          >
            <option value="">정책 해제</option>
            {assignmentPlans.map((plan) => (
              <option key={plan.id} value={plan.id}>
                {plan.plan_name} ({formatManagementServicePlan(plan)})
              </option>
            ))}
          </select>
        </div>
      </div>

      {scope === 'units' && (
        <div className="mt-5 border border-gray-200 rounded-md">
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
            <p className="text-sm font-medium text-gray-900">세대 선택</p>
            <button
              type="button"
              onClick={() => setSelectedUnitIds(units.map((unit) => unit.id))}
              className="text-sm text-blue-600 hover:text-blue-800"
            >
              전체 선택
            </button>
          </div>
          <div className="max-h-56 overflow-y-auto divide-y divide-gray-100">
            {units.length === 0 ? (
              <div className="p-4 text-sm text-gray-500">
                {buildingId ? '등록된 세대가 없습니다.' : '건물을 선택하면 세대 목록이 표시됩니다.'}
              </div>
            ) : (
              units.map((unit) => (
                <label key={unit.id} className="flex items-center justify-between px-4 py-3 text-sm">
                  <span className="flex items-center">
                    <input
                      type="checkbox"
                      checked={selectedUnitIds.includes(unit.id)}
                      onChange={() => handleUnitToggle(unit.id)}
                      className="mr-3 h-4 w-4 rounded border-gray-300 text-blue-600"
                    />
                    {unit.unit_number}호
                  </span>
                  <span className="text-gray-500">
                    {unit.service_plan ? unit.service_plan.plan_name : '상위 정책 적용'}
                  </span>
                </label>
              ))
            )}
          </div>
        </div>
      )}

      <div className="mt-5 flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
        <div className="text-sm text-gray-600">
          {selectedOwner && (
            <span className="mr-4">
              임대사업자 기본: {selectedOwner.default_service_plan?.plan_name || '미지정'}
            </span>
          )}
          {selectedBuilding && (
            <span>
              건물 정책: {selectedBuilding.service_plan?.plan_name || '미지정'}
            </span>
          )}
        </div>
        <button
          type="button"
          onClick={handleApply}
          disabled={isLoading || applyMutation.isLoading}
          className="inline-flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50"
        >
          {applyMutation.isLoading ? '저장 중...' : '요금정책 적용'}
        </button>
      </div>
    </div>
  );
};

const ServicePlanPolicyModal = ({ plan, onClose, onSubmit, isLoading }) => {
  const isEdit = !!plan;
  const [formData, setFormData] = useState({
    plan_name: plan?.plan_name || '',
    description: plan?.description || '',
    plan_category: plan?.plan_category || 'regular',
    fee_calculation_type: plan?.fee_calculation_type || 'per_unit_flat',
    base_fee_per_unit: plan?.base_fee_per_unit || '',
    percentage_rate: plan?.percentage_rate || '',
    flat_fee_amount: plan?.flat_fee_amount || '',
    min_fee_amount: plan?.min_fee_amount || '',
    max_fee_amount: plan?.max_fee_amount || '',
    billing_cycle: plan?.billing_cycle || 'monthly',
    billing_day: plan?.billing_day || 1,
    due_days: plan?.due_days || 7,
    effective_from: plan?.effective_from || '',
    effective_to: plan?.effective_to === OPEN_ENDED_DATE ? '' : plan?.effective_to || '',
    additional_fees: parseJsonObject(plan?.additional_fees),
    discount_settings: parseJsonObject(plan?.discount_settings)
  });

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!formData.plan_name.trim()) {
      toast.error('요금정책명을 입력해주세요.');
      return;
    }

    if (formData.fee_calculation_type === 'per_unit_flat' && Number(formData.base_fee_per_unit || 0) <= 0) {
      toast.error('세대당 정액 금액을 입력해주세요.');
      return;
    }

    if (formData.fee_calculation_type === 'monthly_rent_percentage' && Number(formData.percentage_rate || 0) <= 0) {
      toast.error('월세금액 비율을 입력해주세요.');
      return;
    }

    if (['building_flat', 'property_owner_flat'].includes(formData.fee_calculation_type) && Number(formData.flat_fee_amount || 0) <= 0) {
      toast.error('정액 금액을 입력해주세요.');
      return;
    }

    const submitData = {
      ...formData,
      base_fee_per_unit: parseFloat(formData.base_fee_per_unit || 0),
      percentage_rate: parseFloat(formData.percentage_rate || 0),
      flat_fee_amount: parseFloat(formData.flat_fee_amount || 0),
      min_fee_amount: parseFloat(formData.min_fee_amount || 0),
      max_fee_amount: parseFloat(formData.max_fee_amount || 0),
      billing_day: parseInt(formData.billing_day || 1, 10),
      due_days: parseInt(formData.due_days || 7, 10),
      effective_from: formData.effective_from || null,
      effective_to: formData.effective_to || null,
      additional_fees: formData.additional_fees || {},
      discount_settings: formData.discount_settings || {}
    };

    onSubmit(submitData);
  };

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-12 mx-auto p-5 border w-full max-w-2xl shadow-lg rounded-md bg-white">
        <div className="mt-3">
          <h3 className="text-lg font-medium text-gray-900 mb-4">
            {isEdit ? '요금정책 수정' : '요금정책 등록'}
          </h3>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">요금정책명 *</label>
              <input
                type="text"
                required
                value={formData.plan_name}
                onChange={(e) => setFormData({ ...formData, plan_name: e.target.value })}
                className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                placeholder="예: 세대당 기본 수수료"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">설명</label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                rows={3}
                className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                placeholder="정책 적용 기준이나 내부 운영 메모를 입력하세요."
              />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">정책 구분 *</label>
                <select
                  value={formData.plan_category}
                  onChange={(e) => setFormData({ ...formData, plan_category: e.target.value })}
                  className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                  required
                >
                  <option value="economy">Economy</option>
                  <option value="basic">Basic</option>
                  <option value="regular">Regular</option>
                  <option value="premium">Premium</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">계산 기준 *</label>
                <select
                  value={formData.fee_calculation_type}
                  onChange={(e) => setFormData({ ...formData, fee_calculation_type: e.target.value })}
                  className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                  required
                >
                  <option value="per_unit_flat">세대당 정액</option>
                  <option value="monthly_rent_percentage">월세금액 비율</option>
                  <option value="building_flat">건물당 정액</option>
                  <option value="property_owner_flat">임대사업자 월 정액</option>
                </select>
              </div>
            </div>

            {formData.fee_calculation_type === 'per_unit_flat' && (
              <div>
                <label className="block text-sm font-medium text-gray-700">세대당 정액 *</label>
                <input
                  type="number"
                  min="0"
                  step="1"
                  value={formData.base_fee_per_unit}
                  onChange={(e) => setFormData({ ...formData, base_fee_per_unit: e.target.value })}
                  className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                  placeholder="10000"
                />
              </div>
            )}

            {formData.fee_calculation_type === 'monthly_rent_percentage' && (
              <div>
                <label className="block text-sm font-medium text-gray-700">월세금액 비율 *</label>
                <div className="mt-1 flex rounded-md shadow-sm">
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={formData.percentage_rate}
                    onChange={(e) => setFormData({ ...formData, percentage_rate: e.target.value })}
                    className="block w-full border border-gray-300 rounded-l-md px-3 py-2"
                    placeholder="5"
                  />
                  <span className="inline-flex items-center rounded-r-md border border-l-0 border-gray-300 bg-gray-50 px-3 text-sm text-gray-500">%</span>
                </div>
              </div>
            )}

            {['building_flat', 'property_owner_flat'].includes(formData.fee_calculation_type) && (
              <div>
                <label className="block text-sm font-medium text-gray-700">정액 금액 *</label>
                <input
                  type="number"
                  min="0"
                  step="1"
                  value={formData.flat_fee_amount}
                  onChange={(e) => setFormData({ ...formData, flat_fee_amount: e.target.value })}
                  className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                  placeholder="300000"
                />
              </div>
            )}

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">최소 청구금액</label>
                <input
                  type="number"
                  min="0"
                  step="1"
                  value={formData.min_fee_amount}
                  onChange={(e) => setFormData({ ...formData, min_fee_amount: e.target.value })}
                  className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                  placeholder="선택"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">최대 청구금액</label>
                <input
                  type="number"
                  min="0"
                  step="1"
                  value={formData.max_fee_amount}
                  onChange={(e) => setFormData({ ...formData, max_fee_amount: e.target.value })}
                  className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                  placeholder="선택"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">청구 주기 *</label>
                <select
                  value={formData.billing_cycle}
                  onChange={(e) => setFormData({ ...formData, billing_cycle: e.target.value })}
                  className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                >
                  <option value="monthly">월간</option>
                  <option value="quarterly">분기</option>
                  <option value="annual">연간</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">청구일</label>
                <input
                  type="number"
                  min="1"
                  max="28"
                  value={formData.billing_day}
                  onChange={(e) => setFormData({ ...formData, billing_day: e.target.value })}
                  className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">납부기한</label>
                <input
                  type="number"
                  min="1"
                  value={formData.due_days}
                  onChange={(e) => setFormData({ ...formData, due_days: e.target.value })}
                  className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                />
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">적용 시작일</label>
                <input
                  type="date"
                  value={formData.effective_from}
                  onChange={(e) => setFormData({ ...formData, effective_from: e.target.value })}
                  className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">적용 종료일</label>
                <input
                  type="date"
                  value={formData.effective_to}
                  onChange={(e) => setFormData({ ...formData, effective_to: e.target.value })}
                  className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                />
              </div>
            </div>
            <div className="bg-blue-50 border border-blue-100 rounded-md p-3 text-sm text-blue-800">
              청구 생성 시 선택한 계산 기준과 적용 대상에 따라 기본 수수료가 자동 계산됩니다.
            </div>
            <div className="flex justify-end space-x-3 pt-4">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200"
              >
                취소
              </button>
              <button
                type="submit"
                disabled={isLoading}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 disabled:opacity-50"
              >
                {isLoading ? (isEdit ? '수정 중...' : '등록 중...') : (isEdit ? '수정' : '등록')}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ManagementServicePlanPolicy;
